import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { QuickSearchService } from '@app/core/services/quick-search.service';
import { Utilities } from '@app/core/utils/utilities';
import { InstitutionService } from '@app/pages/institution/service/institution.service';
import { InstitutionConstants } from '@app/pages/institution/utils/institution-constants';
import { SuccessOrWarningPopupComponent } from '@app/shared/components/success-or-warning-popup/success-or-warning-popup.component';
import { ImageUrls } from '@app/shared/constants/image-urls-const';
import { CourseInstanceListingDetails } from '@app/shared/enm/course-instance.enum';
import { ApiService } from '@app/shared/services/api.service';
import { SharedModule } from '@app/shared/shared.module';
import { CommonFunctions } from '@app/shared/util/common-functions';
import { ConstantVariables } from '@app/shared/util/constant-variables';
import { take } from 'rxjs';

@Component({
  selector: 'app-quick-search',
  imports: [SharedModule, MatCardModule, SuccessOrWarningPopupComponent],
  templateUrl: './quick-search.component.html',
  styleUrl: './quick-search.component.css'
})
export class QuickSearchComponent {
  dialogRef = inject(MatDialogRef<QuickSearchComponent>);
  router = inject(Router);
  util = inject(Utilities);
  commonFn = inject(CommonFunctions);
  instService = inject(InstitutionService);
  commonApiService = inject(ApiService);
  quickSearchService = inject(QuickSearchService);
  instConstants = inject(InstitutionConstants);
  imageConstants = inject(ImageUrls);
  constantVariables = inject(ConstantVariables);
  courseInstFilterDetails = CourseInstanceListingDetails;
  userRole = this.util?.getStoredUserDetails()?.roleName;
  currentYear = new Date().getFullYear();
  entityTypes = ['Institution', 'Campus', 'Department', 'Intake', 'Course', 'Course Instance', 'Availability', 'Fee', 'Detailed ER'];
  idTypes: any[] = [{ type: 'SCC ID', isDisabled: false }, { type: 'IDPC legacy ID', isDisabled: false }, { type: 'IDP legacy ID', isDisabled: false }];
  academicYears: number[] = [];
  quickSearchData: any = {};
  submitFlag: boolean = false;
  warningMsg: string = '';
  quickSearchForm: any = new FormGroup({
    entityType: new FormControl('', Validators.required)
  });

  changeEntityType(entityType: string) {
    this.submitFlag = false;
    const fieldList = ['idType', 'entityId', 'academicYear'];
    fieldList.forEach(field => {
      this.addOrRemoveControl(field, 'remove');
    });
    this.idTypes?.forEach(idType => {
      if (['Course', 'Fee'].includes(entityType)) idType.isDisabled = idType.type.includes('legacy');
      else if (['Department', 'Detailed ER'].includes(entityType)) idType.isDisabled = (idType.type === 'IDP legacy ID');
      else idType.isDisabled = ['Intake', 'Availability'].includes(entityType) ? idType.type.includes('IDPC') : false;
    });
    this.addOrRemoveControl('idType', 'add', 'SCC ID');
    this.changeIdType();
  }

  changeIdType() {
    this.submitFlag = false;
    this.addOrRemoveControl('academicYear', 'remove');
    this.addOrRemoveControl('entityId', 'remove');
    this.addOrRemoveControl('entityId', 'add');
    this.academicYears = [];
    if (this.quickSearchForm.get('entityType')?.value === 'Course Instance' && this.quickSearchForm.get('idType')?.value?.includes('legacy')) {
      const yearRange = 3;
      for (let idx = -yearRange; idx <= yearRange; idx++)
        this.academicYears.push(this.currentYear + idx);
      this.addOrRemoveControl('academicYear', 'add');
    }
  }

  addOrRemoveControl(field: string, action: string, fieldValue?: string) {
    if (action === 'add') {
      const validators = (field !== 'academicYear') ? Validators.required : null;
      this.quickSearchForm?.addControl(field, new FormControl(fieldValue ?? '', validators));
    } else {
      this.quickSearchForm?.removeControl(field);
    }
  }

  searchData() {
    this.showWarningMsg();
    this.submitFlag = this.quickSearchForm.invalid;
    if (!this.submitFlag)
      this.redirectPage();
  }

  redirectPage() {
    const searchData = this.quickSearchForm?.value;
    let idType: string = '';
    if (searchData?.idType?.includes('IDP')) idType = searchData?.idType?.includes('IDPC') ? 'IDPC' : 'CN';
    const entityType = searchData?.entityType?.replace(' ', '_');
    this.quickSearchService.getQuickSearchData(entityType, searchData?.entityId?.trim(), idType, searchData?.academicYear).subscribe({
      next: (res: any) => {
        const institutionId = res?.institutionId;
        if (institutionId) {
          this.quickSearchData = res;
          if ((searchData?.entityType === 'Institution') || (localStorage.getItem("institutionDetails") && institutionId === JSON.parse(localStorage.getItem("institutionDetails") ?? '')?.institutionId))
            this.navigateToDetailsPage(searchData);
          else
            this.getInstitutionData(institutionId, searchData);
        } else {
          this.showWarningMsg('No Record Found');
        }
      },
      error: ((err: any) => {
        console.log('get quick search details error-', err)
      })
    });
  }

  getInstitutionData(institutionId: any, searchData: any) {
    this.instService.getInstitutionDetails(institutionId).subscribe({
      next: ((response: any) => {
        const oipFlags = response?.institutionChannels?.filter((flagData: any) => this.instConstants.oipFlagIds.includes(flagData?.channelId))?.some((channelData: any) => channelData?.channelValue);
        const advertFlags = response?.institutionChannels?.filter((flagData: any) => this.instConstants.advertFlagIds.includes(flagData?.channelId))?.some((channelData: any) => channelData?.channelValue);
        const tneFlag = response?.institutionChannels?.find((flagData: any) => (flagData?.channelId === 75))?.channelValue || false;
        const applicableToStudyBC = response?.attributes?.find((attrDetails: any) => attrDetails.attributeId === this.instConstants.institutionAttributeIds.applicableToStudyBC)?.attributeValue === this.constantVariables.radioTypeYes;
        const clearingProviderFlag = Boolean(response?.institutionChannels?.find((flagData: any) => flagData?.channelId === this.instConstants.clearingProviderFlagId)?.channelValue);
        const ucasProviderFlag = Boolean(response?.institutionChannels?.find((flagData: any) => flagData?.channelId === this.instConstants.ucasProviderFlagId)?.channelValue);

        this.util.institutionDetails.set('');
        localStorage.setItem('institutionDetails', JSON.stringify({
          institutionId: institutionId, institutionName: response?.institutionDisplayName, instPageUrl: ((this.userRole === 'SCC View Only' || response?.status?.toLowerCase() === 'archived') ? '/view-institution/' : '/edit-institution/') + institutionId,
          status: response.status, fastLaneFlag: oipFlags, advertFlag: advertFlags, institutionChannels: tneFlag,
          eflCoursesAvailability: response?.eflCoursesAvailability || false, applicableToStudyBC, clearingProviderFlag, ucasProviderFlag
        }));
        this.util.getInstituionDetails();
        this.getCountryData(response?.institutionCountryId);
        this.navigateToDetailsPage(searchData);
      }),
      error: ((err: any) => {
        console.log('get inst details error-', err)
      })
    });
  }

  getCountryData(institutionCountryId: any) {
    this.commonApiService.getCountryData().pipe(take(1)).subscribe((data: any) => {
      const filteredCountry = data.countries?.find((country: any) => country?.countryId === institutionCountryId);
      localStorage.setItem('countryDetails', JSON.stringify(filteredCountry));
    });
  }

  navigateToDetailsPage(searchData: any) {
    let redirectUrl: string = '';
    const pageType = (this.userRole === 'SCC View Only') ? '/view' : '/edit';
    switch (searchData?.entityType) {
      case 'Institution':
        redirectUrl = pageType + '-institution/' + this.quickSearchData?.institutionId;
        break;
      case 'Campus':
        redirectUrl = pageType + '-campus/' + this.quickSearchData?.campusId;
        break;
      case 'Department':
        redirectUrl = pageType + '-department/' + this.quickSearchData?.departmentId;
        break;
      case 'Intake':
        redirectUrl = pageType + '-intake/' + this.quickSearchData?.intakeId;
        break;
      case 'Course':
        redirectUrl = pageType + '-course/' + this.quickSearchData?.courseTypeId + '/' + this.quickSearchData?.courseId;
        break;
      case 'Course Instance': {
        if (searchData?.idType?.toLowerCase()?.includes('scc') || searchData?.academicYear) {
          redirectUrl = pageType + '-course/' + this.quickSearchData?.courseTypeId + '/' + this.quickSearchData?.courseId + '/' + this.quickSearchData?.courseInstanceId;
        } else {
          this.util.quickSearchDetails.set('');
          redirectUrl = '/manage-course/instance/' + this.quickSearchData?.courseTypeId + '/' + this.quickSearchData?.courseId;
          this.util.quickSearchDetails.set({ entityType: searchData?.entityType, [(searchData?.idType?.includes('IDPC')) ? this.courseInstFilterDetails.idpcLegacyCourseId : this.courseInstFilterDetails.idpCNCourseId]: searchData?.entityId?.trim() });
        }
        break;
      }
      case 'Availability':
      case 'Fee': {
        this.util.quickSearchDetails.set('');
        redirectUrl = '/course-availability/' + this.quickSearchData?.courseTypeId + '/' + this.quickSearchData?.courseId + '/' + this.quickSearchData?.courseInstanceId;

        let searchDetails = { entityType: searchData?.entityType, selectedCampus: this.quickSearchData?.campusId, selectedYear: this.quickSearchData?.academicYear, availabilityId: this.quickSearchData?.availabilityId, redirectUrl };
        if (searchData?.entityType === 'Fee')
          searchDetails = Object.assign(searchDetails, { feeId: this.quickSearchData?.feeId, feeTypeId: this.quickSearchData?.feeTypeId, duration: this.quickSearchData?.duration, feeUnit: this.quickSearchData?.feeUnit });
        this.util.quickSearchDetails.set(searchDetails);
        break;
      }
      case 'Detailed ER':
        redirectUrl = pageType + '-er/' + this.quickSearchData?.erQualificationTypeId + '/' + this.quickSearchData?.erQualificationId;
        break;
    }
    this.dialogRef.close();
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.navigateByUrl(redirectUrl);
  }

  showWarningMsg(message?: string) {
    this.warningMsg = message ?? '';
    if (this.warningMsg)
      setTimeout(() => {
        this.warningMsg = '';
      }, 10000);
  }

  fieldValueRestriction(event: any) {
    if (['SCC ID', 'IDPC legacy ID'].includes(this.quickSearchForm?.get('idType')?.value)) {
      let value = (event.target as HTMLTextAreaElement)?.value?.trim();
      value = value?.replace(/[^\d]/g, '');
      this.quickSearchForm?.get('entityId')?.reset(value);
    }
  }
}